/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Button from '@mui/material/Button'
import { CreateQuestionBankCategoryInput } from '__generatedTypes__/globalTypes'
import { InputField } from 'components/Input'
import { Tooltip } from 'components/Tooltip'
import { validateRequired } from 'utils/validation'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import { toast } from 'utils/toast'
import { QuestionBankCategory } from '../useCategoriesApi'
import * as classes from './styles'

export type Props = {
  categoryOrSubjectId: QuestionBankCategory | string
  onSubmit: (category: CreateQuestionBankCategoryInput) => Promise<void>
  onCancel?: () => void
  onRemove?: () => void
  defaultOrder?: number
  submitting?: boolean
}

const focusOnErrors = createDecorator<QuestionBankCategory, Partial<QuestionBankCategory>>()

export const prepareForSubmit = (values: Partial<QuestionBankCategory>): CreateQuestionBankCategoryInput => ({
  title: values.title,
  subject: values.subject ? { id: values.subject.id } : undefined,
  membershipLevel: values.membershipLevel,
  order: values.order,
  isLive: values.isLive ?? false,
})

export const getInitialValues = (
  categoryOrSubjectId: QuestionBankCategory | string,
  defaultOrder = 0
): Partial<QuestionBankCategory> => {
  if (typeof categoryOrSubjectId === 'string') {
    return {
      subject: { id: categoryOrSubjectId } as QuestionBankCategory['subject'],
      order: defaultOrder,
    }
  }

  return {
    ...categoryOrSubjectId,
  }
}

export const Form = ({ categoryOrSubjectId, onSubmit, onCancel, onRemove, defaultOrder = 0, submitting }: Props) => {
  const [initialValues, setInitialValues] = useState(getInitialValues(categoryOrSubjectId, defaultOrder))
  const [isWarningVisible, setIsWarningVisible] = useState(false)

  useEffect(() => {
    setIsWarningVisible(false)
    setInitialValues(getInitialValues(categoryOrSubjectId, defaultOrder))
  }, [categoryOrSubjectId])

  const handleRemoveClick = useCallback(() => {
    if (!initialValues.id) {
      return
    }

    if (isWarningVisible) {
      onRemove?.()
    } else {
      setIsWarningVisible(true)
    }
  }, [isWarningVisible])

  const handleSubmit = useCallback(
    async (values: QuestionBankCategory) => {
      try {
        const categoryData = await prepareForSubmit(values)
        await onSubmit(categoryData)
      } catch (e) {
        toast.error('Failed to prepare data for submit')
      }
    },
    [onSubmit]
  )

  return (
    <FinalForm<QuestionBankCategory>
      mutators={{ ...arrayMutators }}
      decorators={[focusOnErrors]}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, initialValues, submitting: formSubmitting }) => (
        <form onSubmit={handleSubmit} css={classes.content}>
          <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />

          <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />

          <div css={classes.buttonsWrapper}>
            {!!onCancel && (
              <Button
                variant="outlined"
                color="blue"
                disabled={formSubmitting || submitting}
                size="large"
                onClick={onCancel}
                fullWidth
              >
                Cancel
              </Button>
            )}
            {!!onRemove &&
              (initialValues.isLive ? (
                <Tooltip title="You can't delete Question Bank Category because Live is enabled" isNote arrow>
                  <Button variant="outlined" color="red" size="large" fullWidth disabled>
                    Delete
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  color="red"
                  disabled={formSubmitting || submitting}
                  size="large"
                  onClick={handleRemoveClick}
                  fullWidth
                >
                  Delete
                </Button>
              ))}
            <Button
              type="submit"
              variant="contained"
              color="blue"
              disabled={formSubmitting || submitting}
              size="large"
              fullWidth
            >
              {initialValues.id ? 'Update' : 'Create'}
            </Button>
          </div>

          {isWarningVisible && (
            <div css={classes.warning}>Are you sure you want to delete? If yes, click “Delete” again to delete.</div>
          )}
        </form>
      )}
    />
  )
}
